import { CENTRA_PRICELIST_ID } from "@/data/centra-currency-id"
import { countryData } from "@/data/country-data"
import { CentraLanguageCode, validateLanguageCode } from "@/data/supported-languages"
import {
    CENTRA_MARKET_CODE,
    CentraMarketCode,
    validateMarketCode
} from "@/data/supported-markets"
import {
    CENTRA_PRICELIST_CODE,
    CentraPricelistCode,
    validatePricelistCode
} from "@/data/supported-pricelists"
import centraPath from "@/lib/centra-path/centra-path-init"
import Logger from "@/lib/logger/Logger"
import logger from "@/lib/logger/logger-init"
import { z } from "zod"
import { CentraLocaleShippingCountryCode } from "../data/centra-locale-shipping-country-code"
import {
    CountryCode,
    CountryCommonNamesAndCode,
    validateCountryCode
} from "../data/country-data"
import {
    SHIPPING_COUNTRIES,
    SHIPPING_COUNTRY_ADJECTIVES,
    ShippingCountry
} from "../data/shipping-countries"
import { ShippingCountryCode } from "../data/shipping-country-code"
import centraLocaleShippingCountryCodeToCentraMarketMap, {
    CentraLocaleShippingCountryCodeToCentraMarketMap
} from "./centra-locale-shipping-country-code-to-market-code"
import centraPricelistCodeToCentraPricelistId from "./country-code-to-currency"
import countryCodeToDefaultLanguageMap, {
    CountryCodeToDefaultLanguageMap
} from "./country-code-to-default-language"
import countryToDefaultLanguageMap, {
    CountryToDefaultLanguageMap,
    getDefaultLanguageFromCountry
} from "./country-to-default-language"
import countryToMarketMap, { CountryToMarketMap } from "./country-to-market"
import marketCodeToDefaultCountry, {
    MarketCodeToDefaultCountryMap
} from "./market-code-to-default-country"
import marketCodeToDefaultLanguage, {
    MarketCodeToDefaultLangaugeMap
} from "./market-code-to-default-language"
import marketCodeToMarketIdMap, {
    MarketCodeToMarketIdMap
} from "./market-code-to-market-id"
import marketIdToMarketCodeMap, {
    MarketIdToMarketCodeMap
} from "./market-id-to-market-code"
import pricelistIdToPricelistCodeMap, {
    PricelistIdToPricelistCodeMap
} from "./pricelist-id-to-pricelist-code"
import shippingCountryCodeToPriceList from "./shipping-country-code-to-pricelist"
import { getShippingCountryNameByShippingCountryCode } from "./shipping-country-code-to-shipping-country-name"

const mapMarketIdToMarketCodeCurried =
    (
        logger: Logger,
        marketIdToMarketCodeMap: MarketIdToMarketCodeMap,
        validateMarketCode: (
            code: string
        ) => z.SafeParseReturnType<CentraMarketCode, CentraMarketCode>
    ) =>
    (marketId: number): CentraMarketCode | null => {
        const isValidNumber = (num: number): boolean =>
            typeof num === "number" && !isNaN(num)

        if (!isValidNumber(marketId)) {
            logger.error("Invalid marketId input: must be a number.")
            return null
        }

        const marketCode = marketIdToMarketCodeMap.get(marketId)
        if (!marketCode) {
            logger.error(`No market code found for market ID: ${marketId}`)
            return null
        }

        const result = validateMarketCode(marketCode)
        return result.success ? result.data : null
    }

export const mapMarketIdToMarketCode = mapMarketIdToMarketCodeCurried(
    logger,
    marketIdToMarketCodeMap,
    validateMarketCode
)

const mapMarketCodeToMarketIdCurried =
    (logger: Logger, marketCodeToMarketIdMap: MarketCodeToMarketIdMap) =>
    (marketCode: CentraMarketCode): number | null => {
        if (typeof marketCode !== "string" || marketCode.trim() === "") {
            logger.error("Invalid marketCode input: must be a non-empty string.")

            return null
        }

        const marketEntry = Array.from(marketCodeToMarketIdMap.entries()).find(
            ([code]) => code === marketCode
        )

        return marketEntry ? marketEntry[1] : null
    }

export const mapMarketCodeToMarketId = mapMarketCodeToMarketIdCurried(
    logger,
    marketCodeToMarketIdMap
)

const mapCountryToDefaultLanguageCurried =
    (
        logger: Logger,
        countryToDefaultLanguageMap: CountryToDefaultLanguageMap,
        validateLanguageCode: (
            code: string
        ) => z.SafeParseReturnType<CentraLanguageCode, CentraLanguageCode>
    ) =>
    (country: string): CentraLanguageCode | null => {
        const isValidString = (str: any): str is string =>
            typeof str === "string" && str.trim() !== ""

        const formatCountryString = (country: string): string =>
            country.trim().toLowerCase()

        if (!isValidString(country)) {
            logger.error("Invalid country input")
            return null
        }

        const formattedCountry = formatCountryString(country)
        const languageCode = countryToDefaultLanguageMap.get(
            formattedCountry as ShippingCountry
        )

        if (!languageCode) {
            logger.error(`No default language found for country: ${formattedCountry}`)
            return null
        }

        const result = validateLanguageCode(languageCode)

        return result.success ? result.data : null
    }

export const mapCountryToDefaultLanguage = mapCountryToDefaultLanguageCurried(
    logger,
    countryToDefaultLanguageMap,
    validateLanguageCode
)

const mapCountryCodeToDefaultLanguageCurried =
    (
        logger: Logger,
        countryCodeToDefaultLanguageMap: CountryCodeToDefaultLanguageMap,
        validateLanguageCode: (
            code: string
        ) => z.SafeParseReturnType<CentraLanguageCode, CentraLanguageCode>
    ) =>
    (countryCode: ShippingCountryCode | CountryCode): CentraLanguageCode | null => {
        const isValidString = (str: any): str is string =>
            typeof str === "string" && str.trim() !== ""

        const formatCountryCodeString = (countryCode: string): string =>
            countryCode.trim().toLowerCase()

        if (!isValidString(countryCode)) {
            logger.error("Invalid country input")

            return null
        }

        const formattedCountryCode = formatCountryCodeString(countryCode)

        const languageCode = countryCodeToDefaultLanguageMap.get(
            formattedCountryCode as ShippingCountryCode
        )

        if (!languageCode) {
            logger.error(
                `No default language found for country code: ${formattedCountryCode}`
            )

            return null
        }

        const result = validateLanguageCode(languageCode)

        return result.success ? result.data : null
    }

export const mapCountryCodeToDefaultLanguage = mapCountryCodeToDefaultLanguageCurried(
    logger,
    countryCodeToDefaultLanguageMap,
    validateLanguageCode
)

const mapShippingCountryCodeToDefaultLanguageCurried =
    (
        getShippingCountryNameByCountryCode: (
            countryCode: ShippingCountryCode
        ) => ShippingCountry | undefined,
        getDefaultLanguageFromCountry: (country: ShippingCountry) => CentraLanguageCode
    ) =>
    (countryCode: ShippingCountryCode): CentraLanguageCode => {
        const country = getShippingCountryNameByCountryCode(countryCode)

        return country ? getDefaultLanguageFromCountry(country) : "en"
    }

export const mapShippingCountryCodeToDefaultLanguage =
    mapShippingCountryCodeToDefaultLanguageCurried(
        getShippingCountryNameByShippingCountryCode,
        getDefaultLanguageFromCountry
    )

const mapPricelistCodeToShippingCountryCodeCurried =
    (
        logger: Logger,
        shippingCountryCodeToPriceList: Map<
            CentraLocaleShippingCountryCode,
            (typeof CENTRA_PRICELIST_CODE)[keyof typeof CENTRA_PRICELIST_CODE]
        >,
        euroCurrencyCode: string = ""
    ) =>
    (pricelistCode: CentraPricelistCode): CentraLocaleShippingCountryCode | null => {
        if (pricelistCode === CENTRA_PRICELIST_CODE.eur) {
            return euroCurrencyCode as CentraLocaleShippingCountryCode
        }

        const shippingCountryCode = Array.from(
            shippingCountryCodeToPriceList.entries()
        ).find(([, code]) => code === pricelistCode)?.[0]

        if (!shippingCountryCode) {
            logger.error(
                `No shipping country code found for pricelist code: ${pricelistCode}`
            )
            return null
        }

        return shippingCountryCode
    }

export const mapPricelistCodeToShippingCountryCode =
    mapPricelistCodeToShippingCountryCodeCurried(logger, shippingCountryCodeToPriceList)

/**
 * Returns "EU" as default market code if no market code is found for the given Centra shipping country code.
 */
const mapCentraShippingCountryCodeToCentraMarketCodeCurried =
    (
        centraLocaleShippingCountryCodeToCentraMarketMap: CentraLocaleShippingCountryCodeToCentraMarketMap,
        validateMarketCode: (
            code: string
        ) => z.SafeParseReturnType<CentraMarketCode, CentraMarketCode>
    ) =>
    (countryCode: CentraLocaleShippingCountryCode): CentraMarketCode => {
        const centraMarketCode =
            centraLocaleShippingCountryCodeToCentraMarketMap.get(countryCode)

        if (!centraMarketCode) {
            logger.error(
                `No market found for country code: ${countryCode}. Returning EU as default market code.`
            )

            return CENTRA_MARKET_CODE.Eu
        }

        const marketCodeValidation = validateMarketCode(centraMarketCode)

        if (!marketCodeValidation.success) {
            logger.error(
                `Invalid market code: ${centraMarketCode}. Returning EU as default market code.`
            )

            return CENTRA_MARKET_CODE.Eu
        }

        return marketCodeValidation.data
    }

export const mapCentraShippingCountryCodeToCentraMarketCode =
    mapCentraShippingCountryCodeToCentraMarketCodeCurried(
        centraLocaleShippingCountryCodeToCentraMarketMap,
        validateMarketCode
    )

const mapCountryToMarketCurried =
    (
        logger: Logger,
        countryToMarketMap: CountryToMarketMap,
        validateCountryCode: (
            code: string
        ) => z.SafeParseReturnType<CountryCode, CountryCode>,
        validateMarketCode: (
            code: string
        ) => z.SafeParseReturnType<CentraMarketCode, CentraMarketCode>
    ) =>
    (country: string): CentraMarketCode | null => {
        const isValidString = (str: any): str is string => {
            return typeof str === "string" && str.trim() !== ""
        }

        const formatCountryString = (country: string): string => {
            return (
                country.trim().charAt(0).toUpperCase() +
                country.trim().slice(1).toLowerCase()
            )
        }

        if (!isValidString(country)) {
            logger.error("Invalid country input")
            return null
        }

        const formattedCountry = formatCountryString(country)

        const marketCode = countryToMarketMap.get(formattedCountry)
        if (!marketCode) {
            logger.error(`No market found for country: ${formattedCountry}`)
            return null
        }

        const marketCodeValidation = validateMarketCode(marketCode)

        return marketCodeValidation.success ? marketCodeValidation.data : null
    }

export const mapCountryToMarket = mapCountryToMarketCurried(
    logger,
    countryToMarketMap,
    validateCountryCode,
    validateMarketCode
)

export const mapCountryNameToCountryCodeCurried =
    (logger: Logger) =>
    (countriesData: CountryCommonNamesAndCode[], country: string): CountryCode | null => {
        const isValidInput = (input: string): boolean =>
            typeof input === "string" && input.trim() !== ""

        const normalizeString = (str: string): string => {
            return str
                .replace(/\[.*?\]/g, "")
                .replace(/-/g, " ")
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9 ]/g, "")
        }

        const isCommonNameMatch = (
            data: CountryCommonNamesAndCode,
            countryName: string
        ): boolean => {
            return data.commonNames
                .split(",")
                .map(name => normalizeString(name).trim())
                .includes(countryName)
        }

        if (!isValidInput(country)) {
            logger.error(`Invalid country input: '${country}'`)

            return null
        }

        const countryNormalized = normalizeString(country)

        for (const data of countriesData) {
            if (isCommonNameMatch(data, countryNormalized)) {
                logger.debug(`Country code found for ${country}: ${data.alpha2Code}`)

                return data.alpha2Code as CountryCode
            }
        }

        logger.warn(`No country code found for ${country}`)

        return null
    }

export const mapCountryNameToCountryCode = mapCountryNameToCountryCodeCurried(logger)

export const mapCountryCodeToCountryNameCurried =
    (logger: Logger) =>
    (code: string): string => {
        const fallbackCountryName =
            SHIPPING_COUNTRIES.Germany.charAt(0).toUpperCase() +
            SHIPPING_COUNTRIES.Germany.slice(1).toLowerCase()

        if (typeof code !== "string" || code.trim().length !== 2)
            return fallbackCountryName

        const upperCode = code.trim().toUpperCase()

        const match = countryData.find(c => c.alpha2Code.toUpperCase() === upperCode)

        if (!match) {
            logger.error(
                `No country name found for code: ${upperCode}. Please check the code. Return Default country name.`
            )
            return fallbackCountryName
        }
        const matchedCountryName = match.countryName.split(",")[0].trim()
        return matchedCountryName
    }

export const mapCountryCodeToCountryName = mapCountryCodeToCountryNameCurried(logger)

export function getCountryAdjectiveFromCode(countryCode: CountryCode): string {
    const countryName = mapCountryCodeToCountryName(countryCode)

    if (!countryName) return SHIPPING_COUNTRY_ADJECTIVES.Germany

    const adjective =
        SHIPPING_COUNTRY_ADJECTIVES[
            countryName as keyof typeof SHIPPING_COUNTRY_ADJECTIVES
        ]

    return adjective ?? SHIPPING_COUNTRY_ADJECTIVES.Germany
}

const mapMarketCodeToDefaultLanguageCurried =
    (
        logger: Logger,
        marketCodeToDefaultLanguage: MarketCodeToDefaultLangaugeMap,
        validateMarketCode: (
            code: string
        ) => z.SafeParseReturnType<CentraMarketCode, CentraMarketCode>,
        validateLanguageCode: (
            code: string
        ) => z.SafeParseReturnType<CentraLanguageCode, CentraLanguageCode>
    ) =>
    (marketCode: CentraMarketCode): CentraLanguageCode | null => {
        const marketCodeValidation = validateMarketCode(marketCode)

        if (!marketCodeValidation.success) {
            logger.error("Invalid marketCode input: must be a valid market code.")
            return null
        }

        const defaultLanguage = marketCodeToDefaultLanguage.get(marketCode)

        if (!defaultLanguage) {
            logger.error(`No default language found for market code: ${marketCode}`)
            return null
        }

        const languageCodeValidation = validateLanguageCode(defaultLanguage)
        return languageCodeValidation.success ? languageCodeValidation.data : null
    }

export const mapMarketCodeToDefaultLanguage = mapMarketCodeToDefaultLanguageCurried(
    logger,
    marketCodeToDefaultLanguage,
    validateMarketCode,
    validateLanguageCode
)

const mapMarketCodeToDefaultCountryCurried =
    (
        logger: Logger,
        marketCodeToDefaultCountry: MarketCodeToDefaultCountryMap,
        validateMarketCode: (
            code: string
        ) => z.SafeParseReturnType<CentraMarketCode, CentraMarketCode>,
        validateCountryCode: (
            code: string
        ) => z.SafeParseReturnType<CountryCode, CountryCode>
    ) =>
    (marketCode: CentraMarketCode): CountryCode | null => {
        const marketCodeValidation = validateMarketCode(marketCode)

        if (!marketCodeValidation.success) {
            logger.error("Invalid marketCode input: must be a valid market code.")

            const defaultCountry = marketCodeToDefaultCountry.get("eu") ?? "GB"

            const defaultCountryValidation = validateCountryCode(defaultCountry)

            return defaultCountryValidation.success ? defaultCountryValidation.data : null
        }

        const defaultCountry =
            marketCodeToDefaultCountry.get(marketCode) ??
            marketCodeToDefaultCountry.get("eu") ??
            "GB"

        const countryValidation = validateCountryCode(defaultCountry)

        return countryValidation.success ? countryValidation.data : null
    }

export const mapMarketCodeToDefaultCountry = mapMarketCodeToDefaultCountryCurried(
    logger,
    marketCodeToDefaultCountry,
    validateMarketCode,
    validateCountryCode
)

const mapPricelistIdToPricelistCodeCurried =
    (
        logger: Logger,
        pricelistIdToPricelistCodeMap: PricelistIdToPricelistCodeMap,
        validatePricelistCode: (
            code: string
        ) => z.SafeParseReturnType<CentraPricelistCode, CentraPricelistCode>
    ) =>
    (pricelistId: number): CentraPricelistCode | null => {
        const isValidNumber = (num: number): boolean =>
            typeof num === "number" && !isNaN(num)

        if (!isValidNumber(pricelistId)) {
            logger.error("Invalid pricelistId input: must be a number.")

            return null
        }

        const pricelistCode = pricelistIdToPricelistCodeMap.get(pricelistId)

        if (!pricelistCode) {
            logger.error(`No pricelist code found for pricelist ID: ${pricelistId}`)

            return null
        }

        const result = validatePricelistCode(pricelistCode)

        return result.success ? result.data : null
    }

export const mapPricelistIdToPricelistCode = mapPricelistIdToPricelistCodeCurried(
    logger,
    pricelistIdToPricelistCodeMap,
    validatePricelistCode
)

const mapPricelistCodeToPricelistIdCurried =
    (logger: Logger, pricelistIdToPricelistCodeMap: PricelistIdToPricelistCodeMap) =>
    (pricelistCode: CentraPricelistCode): number | null => {
        if (typeof pricelistCode !== "string" || pricelistCode.trim() === "") {
            logger.error("Invalid pricelistCode input: must be a non-empty string.")

            return null
        }

        const pricelistEntry = Array.from(pricelistIdToPricelistCodeMap.entries()).find(
            ([, code]) => code === pricelistCode.toLowerCase()
        )

        return pricelistEntry ? pricelistEntry[0] : null
    }

export const mapPricelistCodeToPricelistId = mapPricelistCodeToPricelistIdCurried(
    logger,
    pricelistIdToPricelistCodeMap
)

const mapCountryToPricelistCurried =
    (
        logger: Logger,
        countryToMarketMap: CountryToMarketMap,
        marketIdToMarketCodeMap: MarketIdToMarketCodeMap,
        pricelistIdToPricelistCodeMap: PricelistIdToPricelistCodeMap,
        validatePricelistCode: (
            code: string
        ) => z.SafeParseReturnType<CentraPricelistCode, CentraPricelistCode>
    ) =>
    (country: CentraMarketCode): CentraPricelistCode | null => {
        const marketCode = countryToMarketMap.get(country)

        if (!marketCode) {
            logger.error(`No market found for country: ${country}`)
            return null
        }

        const marketIdEntry = Array.from(marketIdToMarketCodeMap.entries()).find(
            ([, code]) => code === marketCode
        )

        if (!marketIdEntry) {
            logger.error(`No market ID found for market code: ${marketCode}`)
            return null
        }

        const pricelistCode = pricelistIdToPricelistCodeMap.get(marketIdEntry[0])
        if (!pricelistCode) {
            logger.error(`No pricelist code found for market ID: ${marketIdEntry[0]}`)
            return null
        }

        const result = validatePricelistCode(pricelistCode)
        return result.success ? result.data : null
    }

export const mapCountryToPricelist = mapCountryToPricelistCurried(
    logger,
    countryToMarketMap,
    marketIdToMarketCodeMap,
    pricelistIdToPricelistCodeMap,
    validatePricelistCode
)

export const mapCentraLocaleToPriceListId = (centraLocale: string) => {
    const isStringEuroCurrencyCentraLocale =
        centraPath.isStringEuroCurrencyCentraLocale(centraLocale)

    // If isStringEuroCurrencyCentraLocale is true, return 1
    if (isStringEuroCurrencyCentraLocale) {
        return CENTRA_PRICELIST_ID.eur
    }

    // If isStringEuroCurrencyCentraLocale is false, split the centraLocale to two based on - and get the last part. Then map the last part to the pricelist id
    const countryCode = centraLocale.split("-")[1] as CentraLocaleShippingCountryCode

    const centraPriceListCode = shippingCountryCodeToPriceList.get(countryCode)

    const priceListId = centraPricelistCodeToCentraPricelistId.get(
        centraPriceListCode as CentraPricelistCode
    )

    return priceListId
}

export const mapCentraLocaleToPriceListCode = (centraLocale: string) => {
    const isStringEuroCurrencyCentraLocale =
        centraPath.isStringEuroCurrencyCentraLocale(centraLocale)

    // If it's a Euro currency locale, return EUR pricelist code
    if (isStringEuroCurrencyCentraLocale) {
        return CENTRA_PRICELIST_CODE.eur
    }

    // Ensure the locale has a country code before splitting
    const parts = centraLocale.split("-")
    const countryCode =
        parts.length > 1 ? (parts[1] as CentraLocaleShippingCountryCode) : undefined

    // Get the CentraPricelistCode based on the countryCode
    const centraPriceListCode = countryCode
        ? shippingCountryCodeToPriceList.get(countryCode)
        : CENTRA_PRICELIST_CODE.eur // Default fallback

    return centraPriceListCode
}
